import styles from './Listing.module.css';

import type { FC } from 'react';
import type { Swiper } from 'swiper';
import type { ApiUserAccount } from 'src/types/userAuth';
import type { PurchaseType } from 'src/components/PurchaseTypeSelector';

import classNames from 'classnames';
import { groupBy, isEmpty } from 'lodash';
import { X } from '@phosphor-icons/react';
import { Pagination } from 'swiper/modules';
import React, { useRef, useState, useEffect } from 'react';
import { SwiperSlide, Swiper as SwiperContainer } from 'swiper/react';
import { useParams, useNavigate, useSearchParams } from 'react-router';

import { useDayFormat } from 'src/hooks/useDayFormat';
import { useEscrowOffer } from 'src/hooks/useEscrowOffer';
import { useQueryScreenState } from 'src/hooks/useQueryScreenState';

import { formatAmount } from 'src/utils/money';
import { saleListingHead } from 'src/utils/pageHead';
import { formatImageUrl } from 'src/utils/format-image';
import { offerFromSearch } from 'src/utils/assistantOffer';
import { carAttributeRows } from 'src/utils/carAttributes';
import { settlementToken } from 'src/utils/settlementToken';
import { describeEscrowOffer } from 'src/utils/escrowOffer';
import { noPhotoAlt, carPhotoAlt } from 'src/utils/imageAlt';
import { resolvePurchaseGate } from 'src/utils/purchaseGate';
import { isSameWalletAddress } from 'src/utils/securityError';
import { isListingUnavailable, describeUnavailability } from 'src/utils/listingAvailability';

import { useListing } from 'src/queries';
import currencyImage from 'src/assets/images/currency.png';

import { Loader } from 'src/components/Loader';
import { ListItem } from 'src/components/ListItem';
import { Image } from 'src/components/themed/Image';
import { Stack } from 'src/components/themed/Stack';
import { Button } from 'src/components/themed/Button';
import { DocumentHead } from 'src/components/DocumentHead';
import { Typography } from 'src/components/themed/Typography';
import { PurchasedByYou } from 'src/components/PurchasedByYou';
import { RequestFailureState } from 'src/components/EmptyState';
import { ActionButton } from 'src/components/themed/ActionButton';
import { DirectPurchaseModal } from 'src/components/DirectPurchaseModal';
import { SafeHtml, listingDescriptionText } from 'src/components/SafeHtml';
import { PurchaseTypeSelector } from 'src/components/PurchaseTypeSelector';
import { DeliveryPurchaseModal } from 'src/components/DeliveryPurchaseModal';

interface ListingProps {
  /** An account session is held on this device. */
  isLoggedIn: boolean;
  /** The account behind it (`/user/auth/me`), once the api has answered — its wallets say whose car this is. */
  account?: ApiUserAccount;
  /** The account holds a linked wallet; `undefined` while it is not known yet (`useSession`). */
  hasLinkedWallet?: boolean;
  isTMA?: boolean;
  /**
   * A session is held on this device and is still being checked (`useSession`). The page is on
   * screen meanwhile — the server rendered it — so the buy control waits rather than offer a
   * signed-in buyer "Sign up to buy".
   */
  sessionPending?: boolean;
}

export const Listing: FC<ListingProps> = (props) => {
  const { isLoggedIn, account, hasLinkedWallet, isTMA, sessionPending } = props;
  const [deliveryVisible, setDeliveryVisible] = useState<boolean>(false);
  const [directVisible, setDirectVisible] = useState<boolean>(false);
  const [purchaseType, setPurchaseType] = useState<PurchaseType>('escrow');
  const { id: listingId } = useParams();
  const [searchParams] = useSearchParams();
  // The assistant's "Prepare the offer" (api#425): the address asks for the delivery offer, with a city.
  const offerFromAddress = offerFromSearch(searchParams);
  const offerOpened = useRef(false);
  const swiperRef = useRef<Swiper | null>(null); // Reference to the Swiper instance
  const query = useListing(listingId!);
  const { data: item, refetch } = query;
  const navigate = useNavigate();
  // A car that is gone (404) used to be a white screen with no tab bar and no way out (BUG-03);
  // this screen hides the tab bar, so the failure states below carry their own way back.
  const state = useQueryScreenState(query);
  const dayOf = useDayFormat();
  // The seller's own car: the listing hangs on a wallet, and that wallet is one of the account's.
  const isMine = !!account?.wallets?.some((wallet) =>
    isSameWalletAddress(wallet.walletAddress, item?.seller?.walletAddress)
  );
  // The "third buyer" scenario: the page was open while someone else bought the car.
  // The detail endpoint derives availability from escrow + Buy Now state.
  const availability = item?.availability;
  const unavailability = isListingUnavailable(availability)
    ? describeUnavailability(availability)
    : null;

  // The api names only the logists the dealer may still choose (api#373, api#374): nothing to filter here.
  const groupedPartners = groupBy(item?.partners, 'type');

  const handleClose = () => {
    navigate('/', { replace: true });
  };

  const fromPath = `/listing/${listingId}`;

  // Anonymous → register first (§5 gate).
  const handleRegister = () => {
    navigate(`/register?plan=personal&from=${encodeURIComponent(fromPath)}`);
  };

  // An account with no wallet links one in Profile (Linked wallets), since the deal itself needs
  // a wallet to pay from (decision #5) — and comes back here through the tab bar.
  const handleLinkWallet = () => {
    navigate('/profile');
  };

  // account-first: an account is the first barrier (§5 gate); every buyer command resolves the
  // account session to its primary wallet, so an account with none is sent to link one.
  const purchaseGate = resolvePurchaseGate({ isLoggedIn, hasLinkedWallet, isTMA: !!isTMA });

  // Past the gate, the escrow purchase is offered only where the registry says this buyer can pay
  // for it (app#176): the chain that will host the deal must be one the account has a wallet in.
  const escrowOffer = useEscrowOffer({
    vertical: 'delivery',
    listingCurrency: item?.currency,
    purchaseGate,
  });
  const escrowUnavailable =
    escrowOffer.kind === 'offered' || !item
      ? undefined
      : describeEscrowOffer(escrowOffer, 'Buying with delivery', item.currency);
  // With the escrow purchase withheld, Buy Now is the only mode — whatever the card state says.
  const effectivePurchaseType: PurchaseType = escrowUnavailable ? 'direct' : purchaseType;

  // The delivery offer the assistant prepared opens by itself, once: only past the same gate the
  // Buy button stands behind (an account with a linked wallet, a car that can still be bought and
  // is not the viewer's own, the escrow offered for it). Anything short of that leaves the page
  // as it always is — the address can start a form, never bypass a gate.
  const canOpenOffer =
    offerFromAddress.delivery &&
    !!item &&
    !sessionPending &&
    purchaseGate === 'buy' &&
    !unavailability &&
    !isMine &&
    escrowOffer.kind === 'offered';
  useEffect(() => {
    if (canOpenOffer && !offerOpened.current) {
      offerOpened.current = true;
      setPurchaseType('escrow');
      setDeliveryVisible(true);
      // The address has done its work: without the query a reload or a return to the page does not
      // open the form again on a person who closed it. The point the form starts with is already read.
      navigate(`/listing/${listingId}`, { replace: true });
    }
  }, [canOpenOffer, listingId, navigate]);

  return (
    <>
      {item ? (
        <DocumentHead {...saleListingHead(item, listingDescriptionText(item.description))} />
      ) : (
        <DocumentHead title="Car for sale" />
      )}
      {deliveryVisible && item ? (
        <DeliveryPurchaseModal
          visible={deliveryVisible}
          onClose={() => setDeliveryVisible(false)}
          listing={item}
          initialPointId={offerFromAddress.pointId ?? undefined}
        />
      ) : null}
      {directVisible && item ? (
        <DirectPurchaseModal
          visible={directVisible}
          onClose={() => setDirectVisible(false)}
          listing={item}
        />
      ) : null}
      <div className={classNames('container', styles.container)}>
        {state === 'loading' ? (
          <Loader />
        ) : state !== 'ready' || !item ? (
          <Stack width="100%" p={4} pb={20} maxWidth="750px">
            <RequestFailureState
              titleAs="h1"
              kind={state === 'ready' ? 'failed' : state}
              subject="This listing"
              onBack={handleClose}
              backLabel="Back to cars"
              onRetry={() => refetch()}
            />
          </Stack>
        ) : (
          <Stack width="100%" gap={2} p={4} pb={20} maxWidth="750px">
            <Stack
              width="100%"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              pl={4}
            >
              <Typography as="h1" fontWeight="600" variant="title3" color="white">
                {item.make.name} {item.model.name}
              </Typography>
              <ActionButton onClick={handleClose} label="Back to cars">
                <X size={24} color="white" />
              </ActionButton>
            </Stack>
            <ListItem
              title={`${item.make.name} ${item.model.name} ${item.year}`}
              description={item.vin}
              // The price IS the amount: the settling token is pegged to the listing's
              // currency, so no rate takes part (settlement-amount spec §2) — USDC for a dollar
              // car, EURC for a euro one, whatever token the listing's record still names.
              amount={item.price}
              currency={settlementToken(item.currency)}
              stars={item.price}
              fiatCurrency={item.currency}
              image={
                !isEmpty(item.images) ? (
                  <Stack width="100%">
                    <div className={styles.swiper}>
                      <SwiperContainer
                        modules={[Pagination]}
                        pagination={{ clickable: false }}
                        allowTouchMove
                        onSwiper={(swiper) => (swiperRef.current = swiper)}
                        height={320}
                      >
                        {item.images?.map((i, index, all) => (
                          <SwiperSlide key={i}>
                            <img
                              src={formatImageUrl(i)}
                              alt={carPhotoAlt(item, index, all.length)}
                              className={styles.image}
                            />
                          </SwiperSlide>
                        ))}
                      </SwiperContainer>
                    </div>
                  </Stack>
                ) : (
                  <img
                    src={currencyImage}
                    alt={noPhotoAlt(item)}
                    className={classNames([styles.image, styles.defaultImage])}
                  />
                )
              }
            />
            <Stack ph={4}>
              {item.color && (
                <Typography color="lightWhite" variant="small">
                  Color: {item.color}
                </Typography>
              )}
              {item.mileage != null && (
                <Typography color="lightWhite" variant="small">
                  Mileage: {formatAmount(item.mileage)} km
                </Typography>
              )}
              {carAttributeRows(item).map(({ label, value }) => (
                <Typography key={label} color="lightWhite" variant="small">
                  {label}: {value}
                </Typography>
              ))}
              <Typography color="white">
                <SafeHtml html={item.description} />
              </Typography>
              <Typography color="lightWhite" variant="small">
                Created at: {dayOf(item.createdAt)}
              </Typography>
            </Stack>
            <Stack gap={6} mt={4} mb={4}>
              {Object.keys(groupedPartners).map((type) => (
                <Stack key={type} gap={2}>
                  <Typography color="white" variant="large" pl={4}>
                    This seller works with {type} services:
                  </Typography>
                  {groupedPartners[type].map((partner) => (
                    <ListItem
                      key={partner.id}
                      // A partner without a logo shows none, not a broken picture of `<cdn>/null`.
                      image={
                        partner.logo ? (
                          <Image size="small" src={formatImageUrl(partner.logo)} alt={`${partner.name} logo`} />
                        ) : undefined
                      }
                      title={partner.name}
                      description={partner.description}
                    />
                  ))}
                </Stack>
              ))}
            </Stack>
            {unavailability ? (
              // Sold / reserved / in escrow: no buy controls, explain why instead — unless the
              // viewer is the one who bought it, who gets their receipt and a way back to the deal
              // instead of being told about their own purchase in the third person (UI §4).
              <PurchasedByYou
                listingId={item.id}
                listingCurrency={item.currency}
                // Only for someone who could own an order at all: the two lookups behind this are
                // buyer-only endpoints, and firing them for every anonymous visitor of a sold car
                // buys two guaranteed 403s on the most public screen in the app.
                enabled={isLoggedIn}
                fallback={
                  <Stack gap={1} className={styles.unavailable}>
                    <Typography color="white" variant="large" fontWeight="600">
                      {unavailability.title}
                    </Typography>
                    <Typography color="lightWhite" variant="small">
                      {unavailability.description}
                    </Typography>
                  </Stack>
                }
              />
            ) : sessionPending ? (
              <Button
                onClick={() => undefined}
                aria-label="Checking your session"
                variant="accent"
                className={styles.buyButton}
                loading
                disabled
              />
            ) : purchaseGate === 'buy' ? (
              <>
                {!isMine ? (
                  <PurchaseTypeSelector
                    selectedType={effectivePurchaseType}
                    onSelect={setPurchaseType}
                    escrowUnavailable={escrowUnavailable}
                  />
                ) : null}
                <Button
                  onClick={() =>
                    effectivePurchaseType === 'direct'
                      ? setDirectVisible(true)
                      : setDeliveryVisible(true)
                  }
                  // The chosen card above already says which way; the button used to repeat
                  // it ("Buy now: <car>" / "Buy with delivery: <car>") — Tim, 2026-10-24.
                  title={isMine ? 'This is your listing' : 'Buy'}
                  variant="accent"
                  className={styles.buyButton}
                  disabled={isMine}
                />
              </>
            ) : purchaseGate === 'open-in-browser' ? (
              <Button
                onClick={() => window.open(`${window.location.origin}/listing/${listingId}`, '_blank')}
                title="Open in browser"
                variant="accent"
                className={styles.buyButton}
              />
            ) : purchaseGate === 'link-wallet' ? (
              <Button
                onClick={handleLinkWallet}
                title="Link a wallet to buy"
                variant="accent"
                className={styles.buyButton}
              />
            ) : (
              <Button
                onClick={handleRegister}
                title="Sign up to buy"
                variant="accent"
                className={styles.buyButton}
              />
            )}
          </Stack>
        )}
      </div>
    </>
  );
};
